import { useQuery } from "@tanstack/react-query";
import { tutorApiService } from "../../services/tutorApi";
import { ChatMessage } from "../../types/tutor.types";
import { Mode } from "../../types";

/**
 * useChatHistoryQuery
 *
 * React Query를 사용한 채팅 히스토리 조회 Hook
 *
 * 기능:
 * - 그룹 ID로 채팅 히스토리 자동 캐싱
 * - 중복 요청 자동 방지
 * - 백그라운드 refetch
 *
 * 사용:
 * ```
 * const { data: chatHistory, isLoading } = useChatHistoryQuery({
 *   mode: Mode.TUTOR,
 *   groupId: "123"
 * });
 * ```
 */

interface UseChatHistoryQueryOptions {
  mode: Mode;
  groupId: string | null;
}

export const useChatHistoryQuery = (options: UseChatHistoryQueryOptions) => {
  const { mode, groupId } = options;

  const queryKey = ["chat-history", mode, groupId];

  return useQuery<ChatMessage[]>({
    queryKey,
    queryFn: async () => {
      if (!groupId) {
        return [];
      }

      const history = await tutorApiService.fetchChatHistory(mode, groupId);
      return history;
    },
    enabled: !!groupId, // groupId가 있을 때만 쿼리 실행
    staleTime: 1000 * 60 * 5, // 5분 동안 fresh 상태 유지
    refetchOnWindowFocus: true, // 창 포커스 시 refetch
    refetchOnMount: true, // 마운트 시 refetch
  });
};
